import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Drawer, Radio, Tooltip } from "antd";
import { MenuOutlined, LogoutOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { ReactComponent as VrtsLogo } from "../../Assets/svg/VRTS_Logo.svg";
import { clearLogin, getInitials, getUserName, isAdmin, modeFromPath } from "../../lib/auth";

const Bar = styled.header`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	height: var(--lcds-nav-height);
	background: var(--lcds-navy);
	color: #fff;
	display: flex;
	align-items: center;
	padding: 0 32px;
	gap: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
	@media (max-width: 768px) {
		padding: 0 16px;
		gap: 12px;
	}
`;

const Brand = styled(Link)`
	display: flex;
	align-items: center;
	gap: 12px;
	color: #fff;
	text-decoration: none;
	&:hover {
		color: #fff;
	}
	svg {
		height: 44px;
		width: auto;
	}
	span {
		font-size: 13px;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		opacity: 0.85;
		white-space: nowrap;
		@media (max-width: 992px) {
			display: none;
		}
	}
`;

const ModeSwitch = styled(Radio.Group)`
	white-space: nowrap;
	@media (max-width: 992px) {
		display: none;
	}
	.ant-radio-button-wrapper {
		background: transparent;
		color: #fff;
		border-color: rgba(255, 255, 255, 0.35);
		font-size: 13px;
		font-weight: 500;
		height: 32px;
		line-height: 30px;
		&:not(:first-child)::before {
			background-color: rgba(255, 255, 255, 0.35);
		}
		&:hover {
			color: var(--lcds-teal);
		}
	}
	.ant-radio-button-wrapper-checked {
		background: var(--lcds-teal) !important;
		border-color: var(--lcds-teal) !important;
		color: var(--lcds-navy) !important;
	}
`;

const Links = styled.nav`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-left: 8px;
	@media (max-width: 992px) {
		display: none;
	}
`;

const NavLink = styled(Link)`
	color: rgba(255, 255, 255, 0.85);
	padding: 8px 14px;
	border-radius: 6px;
	font-weight: 500;
	font-size: 14px;
	white-space: nowrap;
	border-bottom: 2px solid ${(p) => (p.$active ? "var(--lcds-teal)" : "transparent")};
	border-radius: 0;
	color: ${(p) => (p.$active ? "#fff" : "rgba(255, 255, 255, 0.85)")};
	&:hover {
		color: var(--lcds-teal);
	}
`;

const Right = styled.div`
	margin-left: auto;
	display: flex;
	align-items: center;
	gap: 14px;
`;

const Avatar = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: var(--lcds-teal);
	color: var(--lcds-navy);
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
`;

const UserName = styled.span`
	font-size: 14px;
	font-weight: 500;
	@media (max-width: 1200px) {
		display: none;
	}
`;

const IconButton = styled.button`
	background: transparent;
	border: 1px solid rgba(255, 255, 255, 0.35);
	color: #fff;
	border-radius: 6px;
	height: 36px;
	padding: 0 12px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-family: inherit;
	font-size: 14px;
	&:hover {
		border-color: var(--lcds-teal);
		color: var(--lcds-teal);
	}
`;

const LogoutLabel = styled.span`
	@media (max-width: 576px) {
		display: none;
	}
`;

const Burger = styled(IconButton)`
	display: none;
	@media (max-width: 992px) {
		display: inline-flex;
	}
`;

const DrawerLink = styled(Link)`
	display: block;
	padding: 12px 4px;
	font-size: 16px;
	color: var(--lcds-text);
	border-bottom: 1px solid var(--lcds-border);
	font-weight: ${(p) => (p.$active ? 600 : 400)};
`;

/** Links shown for each mode. */
export const navLinks = (mode) => {
	const links =
		mode === "event"
			? [
					{ to: "/events", label: "Events" },
					{ to: "/events/create", label: "Add event" },
					{ to: "/stats", label: "Results" },
			  ]
			: [
					{ to: "/users/students", label: "Contacts" },
					{ to: "/users/create", label: "Add contact" },
					{ to: "/stats", label: "Results" },
			  ];
	// The fire-extinguisher app has its own users/PINs, so its stats are shown in both modes.
	links.push({ to: "/events/fireExtStats", label: "Fire extinguisher" });
	if (isAdmin()) {
		links.push({ to: "/admin-users", label: "Users" });
	}
	return links;
};

const TopNav = ({ showLinks = true }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [open, setOpen] = React.useState(false);
	const mode = modeFromPath(location.pathname);

	const handleLogout = () => {
		clearLogin();
		navigate("/");
	};

	const switchMode = (e) => {
		navigate(e.target.value === "event" ? "/events" : "/users/students");
	};

	const isActive = (to) => location.pathname === to;
	const links = showLinks ? navLinks(mode) : [];

	return (
		<Bar>
			<Brand to={showLinks ? "/mode-selection" : "/"} aria-label="LCDS Training home">
				<VrtsLogo />
				<span>LCDS Training</span>
			</Brand>

			{showLinks && (
				<>
					<ModeSwitch value={mode} onChange={switchMode} size="small">
						<Radio.Button value="user">User mode</Radio.Button>
						<Radio.Button value="event">Event mode</Radio.Button>
					</ModeSwitch>
					<Links>
						{links.map((l) => (
							<NavLink key={l.to} to={l.to} $active={isActive(l.to)}>
								{l.label}
							</NavLink>
						))}
					</Links>
				</>
			)}

			{showLinks && (
				<Right>
					<Tooltip title={getUserName()}>
						<Avatar>{getInitials()}</Avatar>
					</Tooltip>
					<UserName>{getUserName()}</UserName>
					<IconButton onClick={handleLogout} aria-label="Log out">
						<LogoutOutlined /> <LogoutLabel>Logout</LogoutLabel>
					</IconButton>
					<Burger onClick={() => setOpen(true)} aria-label="Open menu">
						<MenuOutlined />
					</Burger>
				</Right>
			)}

			<Drawer placement="right" visible={open} onClose={() => setOpen(false)} title="Menu">
				{links.map((l) => (
					<DrawerLink key={l.to} to={l.to} $active={isActive(l.to)} onClick={() => setOpen(false)}>
						{l.label}
					</DrawerLink>
				))}
				<DrawerLink
					to={mode === "event" ? "/users/students" : "/events"}
					onClick={() => setOpen(false)}
				>
					Switch to {mode === "event" ? "User mode" : "Event mode"}
				</DrawerLink>
			</Drawer>
		</Bar>
	);
};

export default TopNav;
